import { type ComponentProps, type RefObject, useRef } from 'react';
import { Combobox as ComboboxPrimitive } from '@base-ui/react/combobox';
import { LuCheck, LuChevronDown } from 'react-icons/lu';
import { cn } from 'utils/utils';
import { FieldError as HookFormFieldError } from 'react-hook-form';

interface ComboboxOption {
  value: string;
  label: string;
}

interface ComboboxProps extends Omit<ComponentProps<'input'>, 'value' | 'onChange' | 'onBlur' | 'children'> {
  options: ComboboxOption[];
  value: ComboboxOption | null;
  onValueChange: (value: ComboboxOption | null) => void;
  onBlur?: () => void;
  placeholder?: string;
  emptyText?: string;
  isTouched?: boolean;
  error?: HookFormFieldError;
}

function Combobox({
  options,
  value,
  onValueChange,
  onBlur,
  placeholder,
  emptyText = 'No results found.',
  disabled,
  name,
  isTouched,
  error,
  ...props
}: ComboboxProps) {
  const anchorRef = useRef<HTMLDivElement>(null);

  return (
    <ComboboxPrimitive.Root
      items={options}
      value={value}
      itemToStringLabel={(option) => option.label}
      itemToStringValue={(option) => option.value}
      onValueChange={onValueChange}
      disabled={disabled}
      name={name}
    >
      <ComboboxInput
        anchorRef={anchorRef}
        onBlur={onBlur}
        placeholder={placeholder}
        isTouched={isTouched}
        error={error}
        {...props}
      />
      <ComboboxContent anchorRef={anchorRef} emptyText={emptyText} />
    </ComboboxPrimitive.Root>
  );
}

function ComboboxInput({
  anchorRef,
  className,
  placeholder,
  isTouched,
  error,
  ...props
}: ComponentProps<typeof ComboboxPrimitive.Input> & {
  anchorRef: RefObject<HTMLDivElement | null>;
  isTouched?: boolean;
  error?: HookFormFieldError;
}) {
  return (
    <div className='flex flex-col gap-[6px]'>
      <div ref={anchorRef} className='relative w-full min-w-0'>
        <ComboboxPrimitive.Input
          placeholder={placeholder}
          aria-invalid={!!error}
          className={cn(
            'w-full rounded-[8px] border bg-transparent px-[10px] py-[8px] font-[MuseoSansCyrl] text-[14px] outline-none transition-colors placeholder:text-lightgrey500',
            error ? 'border-red' : 'border-content-secondary',
            'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
            className
          )}
          {...props}
        />
        <ComboboxPrimitive.Trigger className='absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-lg outline-none disabled:opacity-50'>
          <LuChevronDown className='size-4' />
        </ComboboxPrimitive.Trigger>
      </div>
      {isTouched && error && <div className='text-red text-[14px] font-[MuseoSansCyrl]'>{error.message}</div>}
    </div>
  );
}

function ComboboxContent({ anchorRef, emptyText }: { anchorRef: RefObject<HTMLDivElement | null>; emptyText: string }) {
  return (
    <ComboboxPrimitive.Portal>
      <ComboboxPrimitive.Positioner anchor={anchorRef} sideOffset={4} align='start' className='z-50'>
        <ComboboxPrimitive.Popup className='w-(--anchor-width) max-w-(--available-width) overflow-hidden rounded-lg bg-popover font-[MuseoSansCyrl]'>
          <ComboboxPrimitive.Empty className='px-3 py-6 text-center text-sm empty:hidden'>
            {emptyText}
          </ComboboxPrimitive.Empty>
          <ComboboxPrimitive.List className='max-h-(--available-height) overflow-y-auto p-1 empty:p-0'>
            {(option: ComboboxOption) => <ComboboxItem key={option.value} option={option} />}
          </ComboboxPrimitive.List>
        </ComboboxPrimitive.Popup>
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  );
}

function ComboboxItem({ option }: { option: ComboboxOption }) {
  return (
    <ComboboxPrimitive.Item
      value={option}
      className='relative justify-between flex cursor-default items-center rounded-md p-2 text-sm outline-none data-highlighted:bg-accent'
    >
      <span className='min-w-0'>{option.label}</span>
      <ComboboxPrimitive.ItemIndicator>
        <LuCheck className='size-4' />
      </ComboboxPrimitive.ItemIndicator>
    </ComboboxPrimitive.Item>
  );
}

export { Combobox, type ComboboxOption, type ComboboxProps };
